import { createNode, el, resolve } from '@elemaudio/core'
import CompositeProps from '../types/CompositeProps'
import { SerializableInstrumentStem, SerializableSampleMap } from '../utils/CreateGraph'
import { treeAdd } from '../utils/ElementaryUtils'
import { allocateVoices, createInstrumentEventMap } from '../utils/EventMapUtils'

export type PolySamplerProps = {
  stem: SerializableInstrumentStem
  sampleMap: SerializableSampleMap
  ppqn: number
  key: string
  channel: number
  repeat: any
}

const MAX_VOICES = 24

function PolySamplerComposite({ props, children: [baseClock, resetClock] }: CompositeProps) {
  const { repeat, sampleMap, ppqn, key, stem, channel } = props

  const sample = sampleMap
  const mono = stem.melodicSample.autoRelease

  // stem to voice events
  const eventMap = createInstrumentEventMap(stem)
  const voiceData = allocateVoices(
    stem.uniqueId,
    stem.melodicSample?.autoRelease ? 1 : MAX_VOICES,
    eventMap
  )

  // Populate the empty loop with 0
  if (voiceData.length === 0) return resolve(0)

  const path = `${sample.path}:${channel}`

  const voices = voiceData
    .map((voice, index) => {
      if (voice.semitone === null) {
        return null
      }
      // if (voice === 0) return 0
      const sampleKey = mono
        ? `stem:${key}-monosample-${path}`
        : `stem:${key}:voice${index}-sample-${path}`
      // single el.sample node and rely on its own internal cut behavior for mono
      return el.sample(
        { key: sampleKey, path, mode: stem.melodicSample?.autoRelease ? 'trigger' : 'gate' },
        el.sparseq(
          {
            key: `${voice.key}-seq`,
            offset: ppqn,
            seq: voice.gate,
            loop: repeat.repeatSection ? [repeat.repeatStart, repeat.repeatEnd] : false
          },
          baseClock,
          resetClock
        ),
        el.sparseq(
          {
            key: `${voice.key}-playbackRate`,
            offset: ppqn,
            seq: voice.playbackRate,
            loop: repeat.repeatSection ? [repeat.repeatStart, repeat.repeatEnd] : false
          },
          baseClock,
          resetClock
        )
      )
    })
    .filter((voice) => voice !== null)

  // If you call treeAdd with an empty array, elem crashes.
  if (voices.length === 0) {
    return resolve(0)
  }
  return resolve(treeAdd(voices))
}

export default function PolySampler(props: PolySamplerProps, baseClock, resetClock) {
  return createNode(PolySamplerComposite, props, [baseClock, resetClock])
}
